<template>
    <div class="listings">

        <!-- TITLE -->
        <div id="title" class="container" v-html="title" />

        <!-- FILTER -->
        <filter-bar :sortingOptions="sortingOptions" @on-set-alert="onSetAlert">

            <router-link v-if="mapView"
                :to="{name: listViewName, params: { shortRegion }, query: {...$route.query}}"
                class="btn btn-filter btn-map-view">
                    <i class="fas fa-th-list"></i> List View
            </router-link>

            <router-link v-if="!mapView && !(mode == 'sales' && role == 'default')"
                :to="{name: mapViewName, params: { shortRegion }, query: {...$route.query}}"
                class="btn btn-filter btn-map-view">
                    <i class="fas fa-map-marker-alt"></i> Map View
            </router-link>

        </filter-bar>

        <!-- INTRO -->
        <div class="container position-relative mt-2">
            <div id="intro" v-html="intro" />
        </div>

        <div :class="['listings-layout', { 'map-view': mapView, loading: loading }]">

            <div v-if="mapView" class="listings-map">
                <listing-map :token="mapboxToken" :options="mapboxOptions" />
                <div v-if="mode == 'sales'" class="overlay">
                    Showing properties sold in the last 90 days
                </div>
                <!-- :areas="selected.area" -->
            </div>


            <div :class="['listings-grid', { container: !mapView }]">

                <!-- RESULTS -->
                <div v-html="results" />

                <!-- PAGINATION -->
                <Pagination v-if="pages > 1" :page="page" :pages="pages" :disabled="appDisabled" />

                <!-- LOADING OVERLAY -->
                <div class="loading-overlay align-items-start justify-content-center py-5 ">
                    <i class="fas fa-circle-notch fa-spin fa-3x mt-5"></i>
                </div>

            </div>



        </div>

        <div v-if="mode == 'listings' || mode == 'sales'" class="sticky d-md-none">
            <button-alert :mode="mode" @on-set-alert="onSetAlert" />
        </div>

    </div>
</template>

<script>

    /* global appData */

    import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';

    import ListingMap from '@/mls-filter/components/ListingMap.vue';
    import FilterBar from '@/mls-filter/components/filters/FilterBar.vue';
    import Results from '@/mls-filter/components/Results.vue';
    import Pagination from '@/mls-filter/components/Pagination.vue';
    import ButtonAlert from '@/mls-filter/components/ButtonAlert.vue';
    // import FilterSingle from '@/mls-filter/components/filters/FilterSingle.vue';

    export default {

        name: 'listings',

        props: {
            mapView: {
                type: Boolean,
                default: false,
            },
            initialMode: {
                type: String,
                default: 'listings', // listings|opens|sales
            },
        },

        data: () => ({
            // mapView: true,
        }),

        computed: {

            account() {
                return appData.account;
            },

            region() {
                return appData.region;
            },

            sortingOptions() {
                if (this.mapView) {
                    return appData.sorting.options.filter(op => (op.id === 'dst'));
                } else {
                    return appData.sorting.options.filter(op => (op.id !== 'dst'));
                }
            },

            mapboxToken() {
                return appData.mapboxToken;
            },

            mapboxOptions() {
                return {
                    style: appData.mapboxStyleURL,
                    // center: this.mapCenter,  [-123.0770821, 49.3218825],
                    // zoom: this.mapZoom, // 13,
                }
            },

            mapViewName() {
                switch (this.mode) {
                    case 'sales':
                        return 'sales-mls-nv-map';
                    case 'listings':
                    case 'opens':
                    default:
                        return 'listings-mls-nv-map';
                }
            },

            listViewName() {
                switch (this.mode) {
                    case 'sales':
                        return 'sales-mls-nv-results';
                    case 'listings':
                    case 'opens':
                    default:
                        return 'listings-mls-nv-results';
                }
            },

            ...mapState([
                'page',
                'pages',
                'title',
                'counter',
                'intro',
                'results',
                'selected',
                'alerts',
                'mode',
                'role',
                'appDisabled',
                'loading',
                'mapCenter',
                'mapZoom',
                'sorting',
            ]),

            ...mapGetters([
                'serverData',
                'currentHash',
                'shortRegion',
            ]),

        },

        methods: {

            ...mapActions([
                'applySort',
            ]),

            initialize() {

                let mapCenter = null;
                let mapZoom = null;

                if (this.$route.query.lng && this.$route.query.lat) {
                    mapCenter = [this.$route.query.lng, this.$route.query.lat];
                } else if (appData.region == 1) {
                    mapCenter = [-123.0770821, 49.3218825]
                } else if (appData.region == 2) {
                    mapCenter = [-123.15920035645001, 49.33324739322458]
                }

                if (this.$route.query.zoom) {
                    mapZoom = this.$route.query.zoom;
                }

                // Initialize Vuex
                this.$store.commit('INITIALIZE', {
                    endpoints: appData.endpoints,
                    alerts: appData.alerts.slice(),
                    region: appData.region,
                    filters: appData.filters.slice(),
                    selected: { ...appData.selected },
                    sorting: appData.sorting.selected,
                    disabled: appData.disabled,
                    appDisabled: appData.appDisabled,
                    role: appData.role,
                    mode: this.initialMode,
                    mapCenter,
                    mapZoom,
                });
            },

            preload() {

                // Preload results from server
                if (appData.preload && !appData.preload.loaded) {
                    const preload = appData.preload;
                    preload.html = preload.el_results.innerHTML;
                    preload.title = preload.el_title.innerHTML;
                    preload.intro = preload.el_intro.innerHTML;
                    this.$store.commit('SET_RESULTS', preload);
                    preload.el_results.parentNode.removeChild(preload.el_results);
                    preload.el_title.parentNode.removeChild(preload.el_title);
                    preload.el_intro.parentNode.removeChild(preload.el_intro);
                    preload.loaded = true;
                } else {
                    this.$store.dispatch('fetch');
                }
            },

            onSetAlert() {

                if (this.account) {
                    this.$store.dispatch('setAlert');
                } else {
                    const data = {
                        ...this.serverData,
                        region: this.region,
                    };
                    // this.mode is "listings" or "sales"
                    appData.externals.showSignup(this.mode, JSON.stringify(data));
                }
            },
        },

        created() {
            this.initialize();
            this.preload();
        },

        watch: {
            $route(to, from) {
                if (to.name === from.name && from.query.page !== to.query.page) {
                    this.$store.dispatch('changePage', to.query.page);
                    window.scrollTo(0, 0);
                }
            },
            mapView(newVal, oldVal) {
                // switch sorting to the first option whenever we change from map to list or vice-versa
                this.applySort(this.sortingOptions[0].id);
            },
            results(to, from) {
                // observe when results change
            },
        },

        components: {
            FilterBar,
            Pagination,
            ButtonAlert,
            ListingMap,
        },
    };

</script>

<style lang="scss">

    @import "@/common/scss/_variables.scss";
    @import "@/common/scss/_mixins.scss";

    @include media-breakpoint-down(sm) {

        // hide breakcrumbs on small devices
        section.breadcrumbs {
            padding: 0 !important;
            ol { display: none; }
        }

        #title {
            h2 { font-size: 24px; margin: 10px 0; }
        }

        #intro {
            padding-right: 120px;
        }


    }

    .counter {
        position: absolute;
        top: 0;
        right: 15px;
        margin: 0;

        @include media-breakpoint-down(md) {
            display: none;
        }
    }


    .listings-layout {

        position: relative;

        .counter {
            display: none;
        }

        .map-toggle {

            margin-top: 10px;
            margin-bottom: -55px;

            @include media-breakpoint-down(sm) {

                margin-top: 10px;
                margin-bottom: -40px;

                a {
                    font-size: 16px;
                    padding: 5px 10px;
                }
            }

        }

        .loading-overlay {
            display: none;
        }

        &.loading .loading-overlay {
            display: flex !important;
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            background-color: rgba(white, 0.75);
        }

        &.map-view {

            display: flex;
            height: calc(100vh - 195px);

            @include media-breakpoint-up(md) {
                height: calc(100vh - 416px);
            }

            .map-toggle {
                margin: 0;
                position: absolute;
                top: 10px;
                left: 15px;
                border: 1px solid #333;
            }

            .listings-map {

                position: relative;
                width: 100%;

                .overlay {
                    position: absolute;

                    text-align: center;
                    width: 100%;
                    font-size: 20px;
                    bottom: 36px;
                    color: #000;
                    text-shadow: 1px 1px 0px #FFF;
                    @include media-breakpoint-up(sm) {
                        // bottom: 4px;
                    }
                }

                #map {
                    width: 100%;
                    height: 100%;
                }
            }

            .listings-grid {

                display: none;



                @include media-breakpoint-up(md) {

                    display: block;
                    width: 480px;
                    padding: 0 15px;
                    position: relative;
                    overflow-y: scroll;

                    .card-item {
                        width: 100% !important;
                        max-width: 100% !important;
                        .card-details {
                            .stats-stack {
                                font-size: 16px;
                                dl {
                                    line-height: 24px
                                }
                            }
                            .section-opens {
                                display: none;
                                // .listing-opens .open {
                                //     width: percentage(1/2);
                                // }
                            }
                        }
                    }
                }

                @include media-breakpoint-up(lg) {


                    width: 960px;

                    .card-grid {
                        margin: 0 -10px;
                        .card-item {

                            padding: 10px;

                            width: percentage(1/2) !important;
                            max-width: percentage(1/2) !important;

                            .widget-favorite {
                                top: 10px !important;
                                right: 10px !important;
                            }
                            .section-opens {
                                // .listing-opens .open {
                                //     width: percentage(1/1);
                                // }
                            }

                        }
                    }
                }

            }
        }

        .listings-grid {

            @include media-breakpoint-up(md) {
                .card-item {
                    width: percentage(1/2) !important;
                    max-width: percentage(1/2) !important;
                }
            }

            @include media-breakpoint-up(lg) {
                .card-item {
                    width: percentage(1/3) !important;
                    max-width: percentage(1/3) !important;
                }
            }
        }

    }

</style>